/**
 * T975 — a refusal leaves no analytics identifier on her device. PostHog keeps its
 * anonymous id in a `ph_<key>_posthog` cookie (sent with every request to the site) and in
 * local/session storage under `ph_*` / `__ph_*`. `opt_out_capturing` stops sending but
 * leaves all of it, and a page that refuses at load never even loads the SDK — so this
 * clears them by name, SDK-free. Only PostHog's own names; nothing else is touched.
 */
const POSTHOG_KEY = /^(?:ph_|__ph_)/;

export function clearAnalyticsStorage(): void {
  if (typeof document === "undefined") return;
  for (const part of document.cookie.split(";")) {
    const name = part.split("=")[0]?.trim();
    if (!name || !POSTHOG_KEY.test(name)) continue;
    // PostHog writes its cookie on the widest domain the browser accepts, found by trial (on a
    // multi-label public suffix that is three labels long). Guess nothing: expire it host-only
    // and on every parent domain; a browser ignores the attempts it would never accept.
    for (const domain of cookieDomains()) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
  for (const storage of [safeStorage("localStorage"), safeStorage("sessionStorage")]) {
    if (!storage) continue;
    for (const key of Object.keys(storage)) {
      if (POSTHOG_KEY.test(key)) storage.removeItem(key);
    }
  }
}

function cookieDomains(): (string | null)[] {
  const labels = window.location.hostname.split(".");
  const parents = labels.slice(0, -1).map((_label, index) => `.${labels.slice(index).join(".")}`);
  return [null, ...parents];
}

function safeStorage(name: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return window[name];
  } catch {
    return null;
  }
}
